import React, { useState, useEffect } from 'react';
import { AgCharts } from 'ag-charts-react';
import { BarChart3, Lightbulb } from 'lucide-react';
import EvidenceHeader, { evidenceHeaderControlLabelStyle, evidenceHeaderMetaStyle, evidenceHeaderSelectStyle } from '@process-advisor/process-miner/dashboard/shared/EvidenceHeader';
import { modelMetrics, getModelMetrics, getModelMetricsTotal, getAppVersion, getProcessModelTitle, getActivityContext } from '@process-advisor/process-miner/processMiner/processMiner';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import '@process-advisor/process-miner/dashboard/metrics/metricsDashboard.css';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { setFindingContext, openFindingForContext } from '@process-advisor/process-miner/shared/findingContext';

// ==================== CONSTANTS ====================

const SLEEP_DELAY_MS = 50;
const DEFAULT_PERIODS = 6;

const metricsDashboardCache = new Map();

const makeMetricsCacheKey = (periodType, numberOfPeriods) => {
  const modelTitle = getProcessModelTitle ? getProcessModelTitle() : '';
  const appVersion = getAppVersion ? getAppVersion() : '';
  return `${appVersion}::${modelTitle}::${periodType}::${numberOfPeriods}`;
};

const INITIAL_SAMPLE_DATA = [{
  period: "August 2023",
  work_items: 0,
  std_deviation: 0,
  variants: 0,
  activities: 0,
  steps: 0,
  cycle_time: 0,
  avg_customer_comments: 0,
  avg_internal_comments: 0,
  avg_time_spent: 0,
  avg_transitions: 0,
  rework_rate: 0,
  avg_worklogs: 0,
  workflow_alignment: 0,
  compliance: 0,
  avg_cycle_time_started: 0,
  avg_resolution_time: 0,
  avg_touch_time: 0,
  avg_wait_time: 0,
  avg_admin_time: 0,
  avg_queue_time: 0,
  avg_flow_efficiency: 0
}];

// ==================== UTILITY FUNCTIONS ====================

const sleep = ms => new Promise(r => setTimeout(r, ms));

const formatNumber = (num) => {
  const n = Math.round(num ?? 0);
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return n.toLocaleString();
  return n.toString();
};

const formatPercentage = (num) => Math.round((num ?? 0) * 100) + '%';

const formatTime = (seconds) => {
  seconds = seconds ?? 0;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length > 0 ? parts.join(' ') : '0m';
};

const formatCycleTime = (seconds) => {
  seconds = seconds ?? 0;
  const totalHours = seconds / 3600;
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const hours = Math.floor(totalHours % 24);
    return `${days}d ${hours}h`;
  } else if (totalHours >= 1) {
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
  return `${Math.floor(seconds / 60)}m`;
};

const DURATION_METRIC_KEYS = new Set([
  'std_deviation',
  'cycle_time',
  'avg_time_spent',
  'avg_cycle_time_started',
  'avg_resolution_time',
  'avg_touch_time',
  'avg_wait_time',
  'avg_queue_time',
  'avg_admin_time'
]);

const trimFixed = (value, decimals = 2) => {
  const n = Number(value || 0);
  return n.toFixed(decimals).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

const formatDurationForChart = (seconds, durationFormat = 'compact') => {
  const value = Number(seconds || 0);
  if (durationFormat === 'hours') return `${trimFixed(value / 3600)}h`;
  if (durationFormat === 'days') return `${trimFixed(value / 86400)}d`;
  return formatCycleTime(value);
};

const formatDurationForMetric = (metricKey, value, fallbackFormat, durationFormat = 'compact') => {
  if (DURATION_METRIC_KEYS.has(metricKey)) return formatDurationForChart(value, durationFormat);
  return fallbackFormat(value);
};

const formatChartValue = (metricKey, value, durationFormat = 'compact') => {
  const numericValue = Number(value || 0);
  if (DURATION_METRIC_KEYS.has(metricKey)) return formatDurationForChart(numericValue, durationFormat);
  if (metricKey === 'avg_transitions') return numericValue.toFixed(2);
  if (['rework_rate', 'workflow_alignment', 'compliance', 'avg_flow_efficiency'].includes(metricKey)) return `${(numericValue * 100).toFixed(2)}%`;
  if (['avg_customer_comments', 'avg_internal_comments', 'avg_worklogs'].includes(metricKey)) return numericValue.toFixed(1);
  return formatNumber(numericValue);
};

const LIFECYCLE_ONLY_FLOW_METRICS = new Set([
  'avg_cycle_time_started',
  'avg_resolution_time',
  'avg_touch_time',
  'avg_wait_time',
  'avg_admin_time',
  'avg_queue_time',
  'avg_flow_efficiency'
]);

const isLifecycleStatusModel = () => {
  try {
    const ctx = typeof getActivityContext === 'function' ? getActivityContext() : null;
    const kind = String(ctx?.activity_kind || ctx?.analysis_type || '').toLowerCase();
    return kind === 'lifecycle_status' || kind === 'status' || kind.includes('lifecycle');
  } catch (_) {
    return true;
  }
};

const INVALID_IF_ZERO_METRICS = new Set([
  'avg_time_spent',
  'avg_customer_comments',
  'avg_internal_comments',
  'avg_worklogs'
]);

const isMetricValid = (row, metricKey) => {
  if (!row) return true;
  if (LIFECYCLE_ONLY_FLOW_METRICS.has(metricKey) && !isLifecycleStatusModel()) return false;
  if (typeof row[`${metricKey}_valid`] === 'boolean') return row[`${metricKey}_valid`];
  if (row.metric_validity && typeof row.metric_validity[metricKey] === 'boolean') return row.metric_validity[metricKey];
  if (INVALID_IF_ZERO_METRICS.has(metricKey)) return Number(row[metricKey] || 0) > 0;
  return true;
};

const getMetricInvalidReason = (row, metricKey) => {
  if (!row) return '';
  if (LIFECYCLE_ONLY_FLOW_METRICS.has(metricKey) && !isLifecycleStatusModel()) {
    return 'This metric requires a lifecycle/status activity model. It is not meaningful when the activity field is assignment group, assignee, category, or another non-status attribute.';
  }
  return row[`${metricKey}_invalid_reason`] || 'No tracked data available for this metric in the selected period';
};

// ==================== ICON COMPONENTS ====================

const MetricIcons = {
  WorkItems: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
    </svg>
  ),
  Variants: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  ),
  Transitions: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
    </svg>
  ),
  Activities: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H18V0h-2v2H8V0H6v2H3.5C2.66 2 2 2.66 2 3.5v15C2 19.34 2.66 20 3.5 20h17c.84 0 1.5-.66 1.5-1.5v-15C22 2.66 21.34 2 20.5 2zm0 16.5h-17v-11h17v11z"/>
    </svg>
  ),
  CycleTime: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
    </svg>
  ),
  Deviation: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
    </svg>
  ),
  Rework: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,6V9L16,5L12,1V4A8,8 0 0,0 4,12C4,13.57 4.46,15.03 5.24,16.26L6.7,14.8C6.25,13.97 6,13 6,12A6,6 0 0,1 12,6M18.76,7.74L17.3,9.2C17.74,10.04 18,11 18,12A6,6 0 0,1 12,18V15L8,19L12,23V20A8,8 0 0,0 20,12C20,10.43 19.54,8.97 18.76,7.74Z"/>
    </svg>
  ),
  Compliance: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10,17L5,12L6.41,10.58L10,14.17L17.59,6.58L19,8M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
    </svg>
  ),
  Comments: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9Z"/>
    </svg>
  ),
  InternalComments: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.64,14.34L2,22L10.34,21.37C10.88,21.44 11.43,21.5 12,21.5C17.5,21.5 22,17.92 22,13.5C22,9.08 17.5,5.5 12,5.5M8,13.5H16V15H8V13.5M8,11H18V12.5H8V11M8,8.5H18V10H8V8.5Z"/>
    </svg>
  ),
  TimeSpent: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M15.5,12A3.5,3.5 0 0,1 12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12M12,10.5A1.5,1.5 0 0,0 10.5,12A1.5,1.5 0 0,0 12,13.5A1.5,1.5 0 0,0 13.5,12A1.5,1.5 0 0,0 12,10.5Z"/>
    </svg>
  ),
  Worklogs: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
    </svg>
  ),
  CycleStarted: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 3a9 9 0 1 0 8.95 10h-2.02A7 7 0 1 1 13 5v4l5-5-5-5v4z"/>
      </svg>
    );
  },
  ResolutionTime: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 11h-5V11h3V7h2z"/>
      </svg>
    );
  },
  TouchTime: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 1l3 7h7l-5.5 4.2L18.5 21 12 16.8 5.5 21l2-8.8L2 8h7z"/>
      </svg>
    );
  },
  WaitTime: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 1a11 11 0 1 0 11 11A11 11 0 0 0 12 1zm1 11.59 3.71 3.7-1.42 1.42L11 13V6h2z"/>
      </svg>
    );
  },
  QueueTime: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 3h10a2 2 0 0 1 2 2v3H5V5a2 2 0 0 1 2-2zm-2 7h14v4H5zm0 6h14v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"/>
      </svg>
    );
  },
  AdminTime: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6a2 2 0 0 0-2 2v16l4-3h10a2 2 0 0 0 2-2V8zm1 9H7v-2h8zm3-4h-3V4z"/>
      </svg>
    );
  },
  FlowEfficiency: () => {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 17h4V7H3zm7 0h4V3h-4zm7 0h4v-8h-4z"/>
      </svg>
    );
  }
};
const CategoryIcons = {
  Entity: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
    </svg>
  ),
  Process: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/>
    </svg>
  ),
  Collaboration: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  )
};

const getMetricsConfig = (displayData, processLabel='Process', entityLabel='Work Item', sourceSystem='generic') => {
  const isServiceNow = String(sourceSystem || '').toLowerCase().includes('service') || String(getAppVersion?.() || '').toLowerCase().includes('snow');
  const activityLabel = isServiceNow ? 'states / statuses' : 'workflow states';
  const workTimeDescription = isServiceNow
    ? `The average recorded active effort for a ${entityLabel.toLowerCase()}, where that information is available from the source data. This differs from lead time, which includes waiting and queue time. Use it to compare effort with elapsed time — a large gap indicates significant idle or waiting time.`
    : `The average time logged against a ${entityLabel.toLowerCase()} via Jira worklogs. This reflects active effort recorded by assignees and differs from lead time, which includes waiting. Only available if your team logs time. Use this to understand true effort cost and to compare against lead time — a large gap indicates significant idle or waiting time.`;

  const sections = [
    {
      key: 'entity',
      label: `${entityLabel} Metrics`,
      Icon: CategoryIcons.Entity,
      accentColor: '#0052CC',
      metrics: [
        {
          key: 'work_items',
          title: `${entityLabel}s`,
          subtitle: 'Total volume',
          description: `The total number of ${entityLabel.toLowerCase()}s created or active in this period. Use this as the baseline volume denominator when interpreting all other metrics. A sudden spike or drop here is the first signal to investigate — it can explain changes in averages without any real process change.`,
          value: displayData.work_items,
          format: formatNumber,
          icon: MetricIcons.WorkItems,
          color: '#0052CC'
        },
        {
          key: 'cycle_time',
          title: 'Avg Lead Time',
          subtitle: `${entityLabel} resolution`,
          description: `The average time from when a ${entityLabel.toLowerCase()} is created to when it is resolved or closed. This is the end-to-end customer experience metric — it includes all waiting time, not just active work. High lead time relative to touch time indicates excessive queue or wait time. Target: minimise, benchmark against similar workflows.`,
          value: displayData.cycle_time,
          format: formatCycleTime,
          icon: MetricIcons.CycleTime,
          color: '#DE350B'
        },
        {
          key: 'avg_time_spent',
          title: 'Avg Work Time',
          subtitle: `Per ${entityLabel.toLowerCase()}`,
          description: workTimeDescription,
          value: displayData.avg_time_spent,
          format: formatTime,
          icon: MetricIcons.TimeSpent,
          color: '#0747A6'
        },
        {
          key: 'rework_rate',
          title: '% Re-work',
          subtitle: `Of ${entityLabel.toLowerCase()}s`,
          description: `The percentage of ${entityLabel.toLowerCase()}s that transition backwards to a previous workflow state at least once. Rework signals quality problems, unclear acceptance criteria, or premature status changes. Below 10% is healthy; 10–25% warrants investigation; above 25% indicates a systemic quality or process clarity issue. This is one of the highest-impact metrics for process improvement.`,
          value: displayData.rework_rate,
          format: formatPercentage,
          icon: MetricIcons.Rework,
          color: '#BF2600'
        },
        {
          key: 'std_deviation',
          title: 'Std Deviation',
          subtitle: 'Lead time variability',
          description: `How much lead time varies across ${entityLabel.toLowerCase()}s in this period. A low standard deviation means predictable delivery; a high one means some items take much longer than others. When standard deviation is larger than the average lead time itself, the process is effectively unpredictable. Focus on outlier analysis — a small number of extreme cases often drive a high standard deviation.`,
          value: displayData.std_deviation || 0,
          format: formatCycleTime,
          icon: MetricIcons.Deviation,
          color: '#8777D9'
        },
      ]
    },
    {
      key: 'process',
      label: `${processLabel} Metrics`,
      Icon: CategoryIcons.Process,
      accentColor: '#FFAB00',
      metrics: [
        {
          key: 'variants',
          title: `${processLabel} Variants`,
          subtitle: 'Transition combinations',
          description: `The number of distinct paths taken through the workflow — unique sequences of status transitions. A low variant count means the process is standardised; a high count means work is taking many different routes. Fewer variants generally means more predictable outcomes and easier automation. High variant counts often indicate that workflow states are unclear, misused, or that different teams are operating the same workflow differently.`,
          value: displayData.variants,
          format: (val) => String(val ?? 0),
          icon: MetricIcons.Variants,
          color: '#FFAB00'
        },
        {
          key: 'activities',
          title: `${processLabel} Activities`,
          subtitle: 'Unique activities',
          description: `The number of distinct ${activityLabel} that ${entityLabel.toLowerCase()}s actually pass through. This reflects the real complexity of the process — not the number configured in the platform, but the number actually used. A high activity count compared to the expected workflow design suggests states are being used inconsistently or that the process has unnecessary complexity.`,
          value: displayData.activities,
          format: (val) => String(val ?? 0),
          icon: MetricIcons.Activities,
          color: '#00875A'
        },
        {
          key: 'steps',
          title: `${processLabel} Steps`,
          subtitle: 'Transitions between activities',
          description: `The total number of distinct transitions between activities observed in this period. Each step is a unique From → To status pair. More steps means a more complex process map. A high step count relative to activities suggests significant rework loops and non-standard paths. Reducing steps is a key lever for process simplification and automation readiness.`,
          value: displayData.steps,
          format: (val) => String(val ?? 0),
          icon: MetricIcons.Transitions,
          color: '#6554C0'
        },
        {
          key: 'avg_transitions',
          title: `Avg ${entityLabel} Transitions`,
          subtitle: `Per ${entityLabel.toLowerCase()}`,
          description: `The average number of status changes each ${entityLabel.toLowerCase()} goes through before closing. A well-designed workflow typically has 3–6 transitions. Higher numbers indicate rework loops, unnecessary status changes, or items being reopened. This metric is directly related to rework rate — a rising average transitions count is an early warning signal before rework rate climbs.`,
          value: displayData.avg_transitions,
          format: (val) => (val ?? 0).toFixed(1),
          icon: MetricIcons.Transitions,
          color: '#64748B'
        },
      ]
    },
    {
      key: 'flow',
      label: 'Flow & Time Metrics',
      Icon: CategoryIcons.Process,
      accentColor: '#00A3BF',
      metrics: [
        {
          key: 'avg_cycle_time_started',
          title: 'Avg Cycle Time',
          subtitle: 'From work start to done',
          description: `The average time from when active work begins (the first "In Progress" or equivalent active state) to resolution. Unlike Lead Time which starts at creation, Cycle Time excludes initial queue waiting. This is the metric most directly under the delivery team's control. Compare Cycle Time to Lead Time to understand how much of the total customer wait is queue time vs. active processing time.`,
          value: displayData.avg_cycle_time_started || 0,
          format: formatCycleTime,
          icon: MetricIcons.CycleStarted,
          color: '#00A3BF'
        },
        {
          key: 'avg_resolution_time',
          title: 'Avg Resolution Time',
          subtitle: 'Creation to resolution',
          description: `The average time from ${entityLabel.toLowerCase()} creation to the first time it reaches a resolved or done state. Similar to Lead Time but specifically measures time to first resolution — items that are later reopened are measured to their first closure. Use this when distinguishing between time-to-first-response resolution and total lifecycle time matters, such as in incident or support workflows.`,
          value: displayData.avg_resolution_time || 0,
          format: formatCycleTime,
          icon: MetricIcons.ResolutionTime,
          color: '#1D7AFC'
        },
        {
          key: 'avg_touch_time',
          title: 'Avg Touch Time',
          subtitle: 'Active work effort',
          description: `The average time ${entityLabel.toLowerCase()}s spend in active working states (e.g. In Progress, In Review). This is the value-adding portion of the lead time. Comparing Touch Time to Lead Time gives the Flow Efficiency ratio. Low touch time relative to lead time means most of the customer wait is non-value-adding. Requires status types to be classified as Active in the process model.`,
          value: displayData.avg_touch_time || 0,
          format: formatTime,
          icon: MetricIcons.TouchTime,
          color: '#36B37E'
        },
        {
          key: 'avg_wait_time',
          title: 'Avg Wait Time',
          subtitle: 'Waiting states',
          description: `The average time ${entityLabel.toLowerCase()}s spend in explicit waiting states — states where work is paused pending an external input, approval, or dependency (e.g. Waiting for Customer, Pending Approval). High wait time points directly at bottlenecks and handoff delays. Reducing wait time is usually higher leverage than reducing active work time, since waiting represents pure waste. Requires Waiting status types to be classified in the process model.`,
          value: displayData.avg_wait_time || 0,
          format: formatTime,
          icon: MetricIcons.WaitTime,
          color: '#FF8B00'
        },
        {
          key: 'avg_queue_time',
          title: 'Avg Queue Time',
          subtitle: 'Intake / backlog time',
          description: `The average time ${entityLabel.toLowerCase()}s spend in the initial queue or backlog before work begins. This represents the time between a request being made and someone actually starting on it. High queue time is a capacity signal — it means demand is exceeding the team's ability to start work promptly. Requires Open/Queue status types to be classified in the process model.`,
          value: displayData.avg_queue_time || 0,
          format: formatTime,
          icon: MetricIcons.QueueTime,
          color: '#6554C0'
        },
        {
          key: 'avg_admin_time',
          title: 'Avg Admin Time',
          subtitle: 'Resolved / closure delay',
          description: `The average time ${entityLabel.toLowerCase()}s spend in post-resolution administrative states — typically the delay between a ${entityLabel.toLowerCase()} being marked resolved and it being formally closed. This represents administrative overhead with no customer value. High admin time often indicates a manual closure step that could be automated. Requires Closed status types to be classified separately from Resolved in the process model.`,
          value: displayData.avg_admin_time || 0,
          format: formatTime,
          icon: MetricIcons.AdminTime,
          color: '#6B778C'
        },
        {
          key: 'avg_flow_efficiency',
          title: 'Flow Efficiency',
          subtitle: 'Touch time / cycle time',
          description: `The ratio of active touch time to total lead time, expressed as a percentage. A Flow Efficiency of 100% would mean every moment from creation to closure was spent in active work — practically impossible. World-class processes achieve 40–60%. Most organisations are at 5–20%. Low flow efficiency means most of a ${entityLabel.toLowerCase()}'s lifetime is spent waiting. This is the single best summary metric for identifying process waste. Requires status type classifications to be configured.`,
          value: displayData.avg_flow_efficiency || 0,
          format: formatPercentage,
          icon: MetricIcons.FlowEfficiency,
          color: '#00875A'
        },
      ]
    },
    {
      key: 'collaboration',
      label: 'Collaboration & Effort',
      Icon: CategoryIcons.Collaboration,
      accentColor: '#36B37E',
      metrics: [
        {
          key: 'avg_customer_comments',
          title: 'Customer Comments',
          subtitle: `Per ${entityLabel.toLowerCase()}`,
          description: `The average number of customer-facing comments per ${entityLabel.toLowerCase()}. High customer comment volume can indicate unclear communication, slow resolution driving follow-up, or complex issues requiring iteration. A rising trend alongside rising lead time suggests the two are related — customers are chasing status. Only available if comments are tracked and classified in the source data.`,
          value: displayData.avg_customer_comments,
          format: (val) => (val ?? 0).toFixed(1),
          icon: MetricIcons.Comments,
          color: '#FF8B00'
        },
        {
          key: 'avg_internal_comments',
          title: 'Internal Comments',
          subtitle: `Per ${entityLabel.toLowerCase()}`,
          description: `The average number of internal team comments per ${entityLabel.toLowerCase()}. High internal comment volume relative to transitions can indicate collaboration overhead, unclear handoffs, or work that requires significant back-and-forth before progressing. Useful for identifying ${entityLabel.toLowerCase()} types that are consuming disproportionate team attention despite appearing simple by volume.`,
          value: displayData.avg_internal_comments,
          format: (val) => (val ?? 0).toFixed(1),
          icon: MetricIcons.InternalComments,
          color: '#36B37E'
        },
        {
          key: 'avg_worklogs',
          title: 'Average Worklogs',
          subtitle: `Per ${entityLabel.toLowerCase()}`,
          description: `The average number of individual worklog entries per ${entityLabel.toLowerCase()}. Each worklog entry represents a discrete time-logging event. A high worklog count with low avg work time suggests many small time increments logged — typical of support or interruption-driven work. A low count with high avg work time suggests fewer, longer focused sessions. Only available if time tracking is actively used by the team.`,
          value: displayData.avg_worklogs,
          format: (val) => (val ?? 0).toFixed(1),
          icon: MetricIcons.Worklogs,
          color: '#403294'
        },
      ]
    }
  ];

  // ServiceNow keeps SLA analysis separate and now consolidates activity diagnostics
  // into the core Process Metrics view. Hide Jira-specific collaboration/worklog
  // metrics unless the ServiceNow model explicitly marks them valid elsewhere.
  // Workflow Alignment depends on Jira-style happy-path configuration and is not
  // meaningful for ServiceNow incident/status models, so remove it from SNOW.
  if (isServiceNow) {
    return sections
      .filter(section => section.key !== 'collaboration')
      .map(section => ({
        ...section,
        metrics: (section.metrics || []).filter(metric => metric.key !== 'workflow_alignment'),
      }));
  }

  return sections;
};

// ==================== INFO POPOVER COMPONENT ====================

const MetricInfoPopover = ({ metric, onClose }) => {
  if (!metric?.description) return null;
  return (
    <div
      data-metric-popover="true"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#fff',
        border: '1px solid #DFE1E6',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(9,30,66,0.22)',
        padding: '18px 20px',
        width: 340,
        zIndex: 2000,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 6,
          backgroundColor: metric.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <metric.icon />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#172B4D', lineHeight: 1.2 }}>{metric.title}</div>
          <div style={{ fontSize: 11, color: '#6B778C', marginTop: 1 }}>{metric.subtitle}</div>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6B778C', lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
        >
          ×
        </button>
      </div>
      {/* Divider */}
      <div style={{ height: 1, background: '#EBECF0', marginBottom: 12 }} />
      {/* Description */}
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: '#42526E' }}>
        {metric.description}
      </p>
    </div>
  );
};

// ── Popover backdrop ───────────────────────────────────────────────────────────
const PopoverBackdrop = ({ onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0,
      zIndex: 1999,
      backgroundColor: 'rgba(9,30,66,0.30)',
    }}
  />
);

// ==================== MAIN COMPONENT ====================

const MetricsDashboardHistorical = ({AI, refreshKey, processLabel='Process', entityLabel='Work Item'}) => {
  // State Management
  const [data, setData] = useState(INITIAL_SAMPLE_DATA);
  const [totalData, setTotalData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [periodType, setPeriodType] = useState('month');
  const [numberOfPeriods, setNumberOfPeriods] = useState(DEFAULT_PERIODS);
  const [chartDurationFormat, setChartDurationFormat] = useState('compact');
  const [showWorkflowAlignment, setShowWorkflowAlignment] = useState(true);
  const [modelTitle, setModelTitle] = useState('');
  const [activePopover, setActivePopover] = useState(null); // { key, rect }
  const sourceSystem = String(getAppVersion?.() || '').toLowerCase().includes('snow') ? 'servicenow' : 'generic';

  useEffect(() => {
    setModelTitle(getProcessModelTitle());

    if (false) {
      setShowWorkflowAlignment(false);
    }

    const cacheKey = makeMetricsCacheKey(periodType, numberOfPeriods);
    const cached = metricsDashboardCache.get(cacheKey);

    if (cached) {
      setData(cached.data);
      setTotalData(cached.totalData);
      return;
    }

    callModelMetrics(periodType, numberOfPeriods);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActivePopover(null);
        setSelectedMetric(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!activePopover) return;
    const close = (e) => {
      if (!e.target.closest('[data-metric-popover]')) setActivePopover(null);
    };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [activePopover]);

  // Metrics Calculation
  const callModelMetrics = async (periodType, numberOfPeriods, force = false) => {
    const cacheKey = makeMetricsCacheKey(periodType, numberOfPeriods);
    const cached = metricsDashboardCache.get(cacheKey);

    if (!force && cached) {
      setData(cached.data);
      setTotalData(cached.totalData);
      return;
    }

    try {
      await openProgressModal({ status: 'Processing Process Metrics' });
      await sleep(200);
      await modelMetrics(periodType, numberOfPeriods);

      const nextData = getModelMetrics();
      const nextTotalData = getModelMetricsTotal();
      metricsDashboardCache.set(cacheKey, { data: nextData, totalData: nextTotalData });
      setData(nextData);
      setTotalData(nextTotalData);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      closeProgressModal();
    }
  };

  const changePeriodType = (type) => {
    setPeriodType(type);
    callModelMetrics(type, numberOfPeriods, true);
  };

  const changeNumPeriods = (numPeriods) => {
    setNumberOfPeriods(numPeriods);
    callModelMetrics(periodType, numPeriods, true);
  };

  // Data Processing
  const getFilteredData = () => {
    if (periodType === 'year') {
      const yearData = {};
      
      data.forEach(item => {
        const year = item.period.split(' ')[1];
        if (!yearData[year]) {
          yearData[year] = {
            period: year,
            work_items: 0,
            std_deviation: 0,
            variants: 0,
            activities: item.activities,
            steps: 0,
            cycle_time: 0,
            avg_customer_comments: 0,
            avg_internal_comments: 0,
            avg_time_spent: 0,
            avg_transitions: 0,
            rework_rate: 0,
            avg_worklogs: 0,
            workflow_alignment: 0,
            compliance: 0,
            avg_cycle_time_started: 0,
            avg_resolution_time: 0,
            avg_touch_time: 0,
            avg_wait_time: 0,
            avg_admin_time: 0,
            avg_queue_time: 0,
            avg_flow_efficiency: 0,
            metric_validity_counts: {},
            invalid_reason_map: {},
            count: 0
          };
        }
        
        Object.keys(yearData[year]).forEach(key => {
          if (key !== 'period' && key !== 'activities' && key !== 'count' && key !== 'metric_validity_counts' && key !== 'invalid_reason_map') {
            yearData[year][key] += item[key] || 0;
          }
        });

        Object.keys(item.metric_validity || {}).forEach((metricKey) => {
          if (!yearData[year].metric_validity_counts[metricKey]) yearData[year].metric_validity_counts[metricKey] = { valid: 0, total: 0 };
          yearData[year].metric_validity_counts[metricKey].total += 1;
          if (item.metric_validity[metricKey]) yearData[year].metric_validity_counts[metricKey].valid += 1;
          if (item[`${metricKey}_invalid_reason`]) yearData[year].invalid_reason_map[metricKey] = item[`${metricKey}_invalid_reason`];
        });
        yearData[year].count++;
      });

      const aggregatedData = Object.values(yearData).map(year => {
        const avgFields = {};
        Object.keys(year).forEach(key => {
          if (key !== 'period' && key !== 'work_items' && key !== 'count' && key !== 'metric_validity_counts' && key !== 'invalid_reason_map') {
            avgFields[key] = key === 'variants' || key === 'steps' 
              ? Math.round(year[key] / year.count)
              : year[key] / year.count;
          }
        });

        const metricValidity = {};
        Object.keys(year.metric_validity_counts || {}).forEach((metricKey) => {
          const counts = year.metric_validity_counts[metricKey];
          const valid = counts.valid > 0;
          metricValidity[metricKey] = valid;
          avgFields[`${metricKey}_valid`] = valid;
          if (!valid) avgFields[`${metricKey}_invalid_reason`] = year.invalid_reason_map[metricKey] || 'No tracked data available for this metric in the selected period';
        });

        return { ...year, ...avgFields, metric_validity: metricValidity };
      });

      return aggregatedData.slice(-numberOfPeriods);
    }
    
    return data.slice(-numberOfPeriods);
  };

  const filteredData = getFilteredData();
  const historyDisplayData = filteredData[filteredData.length - 1] || data[data.length - 1];
  const displayData = {
    // Cards should show the latest visible period, not the full-model aggregate.
    // Keep totalData as a fallback only for any fields not present in the history row.
    ...(totalData || {}),
    ...(historyDisplayData || {})
  };
  const previousDisplayData = filteredData[filteredData.length - 2];

  const getTrend = (current, previous, metricKey) => {
    if (previous === undefined || previous === null) return null;
    if (previous === 0) {
      if (current === 0) return { direction: 'stable', change: '0' };
      return { direction: 'up', change: '100.0' };
    }

    const lowerIsBetter = new Set([
      'rework_rate',
      'cycle_time',
      'std_deviation',
      'avg_cycle_time_started',
      'avg_resolution_time',
      'avg_wait_time',
      'avg_queue_time',
      'avg_admin_time'
    ]);

    const change = ((Math.abs(current - previous)) / Math.abs(previous) * 100).toFixed(1);

    if (lowerIsBetter.has(metricKey)) {
      if (current < previous) return { direction: 'up', change };
      if (current > previous) return { direction: 'down', change };
      return { direction: 'stable', change: '0' };
    }

    if (current > previous) return { direction: 'up', change };
    if (current < previous) return { direction: 'down', change };
    return { direction: 'stable', change: '0' };
  };

  const getMetricHistory = (metricKey, format) => { 
    return filteredData
      .filter(item => isMetricValid(item, metricKey))
      .map(item => ({
        period: item.period,
        value: item[metricKey],
        formatted: format(item[metricKey])
      }));
  };

  // Chart Rendering
  const renderHistoryChart = (history, color, key) => {
    if (!history || history.length === 0) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B778C', fontSize: '14px' }}>No valid history for this metric in the selected period.</div>;
    }

    const formatValue = (params) => formatChartValue(key, params.value, chartDurationFormat);

    const options = {
      data: history,
      theme: "ag-material",
      width: 600,
      height: 350,
      series: [
        {
          type: 'bar',
          xKey: 'period',
          yKey: 'value',
          fill: color,
          strokeWidth: 0,
          label: { enabled: false }
        },
        {
          type: 'line',
          xKey: 'period',
          yName: key,
          yKey: 'value',
          stroke: '#FF6B6B',
          strokeWidth: 3,
          marker: {
            enabled: true,
            fill: '#FF6B6B',
            stroke: '#FF6B6B',
            strokeWidth: 2,
            size: 8,
            shape: 'circle'
          },
          label: {
            enabled: true,
            fontSize: 12,
            fontWeight: 'bold',
            color: '#333',
            formatter: (params) => formatValue(params)
          }
        }
      ],
      axes: [
        {
          type: 'category',
          position: 'bottom',
          label: { rotation: -45, fontSize: 11, color: '#666' },
          line: { width: 1, color: '#E0E0E0' },
          tick: { width: 1, size: 5, color: '#E0E0E0' }
        },
        {
          type: 'number',
          position: 'left',
          label: {
            fontSize: 11,
            color: '#666',
            formatter: (params) => formatValue(params)
          },
          line: { width: 1, color: '#E0E0E0' },
          tick: { width: 1, size: 5, color: '#E0E0E0' },
          gridStyle: [{ stroke: '#F0F0F0', lineDash: [2, 2] }]
        }
      ],
      tooltip: { enabled: false },
      background: { fill: '#FAFAFA' },
      padding: { top: 20, right: 20, bottom: 20, left: 20 },
      legend: { enabled: false }
    };

    return <AgCharts options={options} />;
  };

  // Get metrics configurations
  const categories = periodType ? getMetricsConfig(displayData, processLabel, entityLabel, sourceSystem) : [];
  const categoriesWithVisibleMetrics = categories
    .map((category) => ({
      ...category,
      metrics: (category.metrics || []).filter((metric) => isMetricValid(displayData, metric.key)),
    }))
    .filter((category) => category.metrics.length > 0);

  // Styles
  const styles = {
    dashboard: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      backgroundColor: '#f8f9fa',
      padding: '0'
    },
    controls: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F7F9FC 100%)',
      padding: '10px 12px',
      marginBottom: '10px',
      borderRadius: '10px',
      border: '1px solid #DFE1E6',
      boxShadow: '0 1px 2px rgba(9, 30, 66, 0.06)',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px'
    },
    controlsLeft: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center'
    },
    controlLabel: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#172b4d',
      whiteSpace: 'nowrap'
    },
    select: {
      padding: '6px 10px',
      fontSize: '13px',
      border: '1px solid #C1C7D0',
      borderRadius: '6px',
      backgroundColor: 'white',
      cursor: 'pointer',
      outline: 'none',
      fontWeight: '400',
      color: '#172b4d',
      minWidth: '120px',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))',
      gap: '10px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #DFE1E6',
      padding: '11px 13px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(9, 45, 66, 0.06)',
      minHeight: '96px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    },
    iconContainer: {
      width: '30px',
      height: '30px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      flexShrink: 0
    },
    metricValue: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#172B4D',
      lineHeight: '1',
      textAlign: 'right'
    },
    metricTitle: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#172B4D',
      margin: '0 0 2px 0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    metricSubtitle: {
      fontSize: '11px',
      color: '#6B778C',
      margin: '0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    trendIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      fontSize: '11px',
      fontWeight: '600',
      padding: '1px 5px',
      borderRadius: '3px',
      flexShrink: 0
    },
    cardDisabled: {
      opacity: 0.55,
      backgroundColor: '#F4F5F7',
      cursor: 'not-allowed'
    },
    metricValueDisabled: {
      color: '#7A869A'
    },
    metricTitleDisabled: {
      color: '#7A869A'
    },
    invalidBadge: {
      fontSize: '10px',
      fontWeight: '600',
      color: '#6B778C',
      backgroundColor: '#EBECF0',
      padding: '1px 5px',
      borderRadius: '3px',
      flexShrink: 0
    },
    saveFindingButton: {
      border: '1px solid #C1C7D0',
      background: '#FFFFFF',
      color: '#0C66E4',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      lineHeight: 1,
      width: '26px',
      height: '26px',
      padding: 0,
      cursor: 'pointer',
      marginLeft: '8px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(9, 45, 66, 0.54)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(9, 45, 66, 0.25)',
      width: '100%',
      maxWidth: '700px',
      maxHeight: '80vh'
    },
    modalHeader: {
      padding: '24px 32px 16px',
      borderBottom: '1px solid #f4f5f7',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#172b4d',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    closeButton: {
      padding: '8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      borderRadius: '6px',
      transition: 'background-color 0.2s'
    },
    modalBody: {
      padding: '20px 32px 32px',
      height: '450px'
    }
  };

  return (
    <div style={styles.dashboard}>
      <EvidenceHeader
        icon={<BarChart3 size={20} />}
        title={`${processLabel} Metrics`}
        subtitle="Track process performance over time"
        actions={(
          <>
            <label style={evidenceHeaderControlLabelStyle}>Period Type:</label>
            <select 
              value={periodType} 
              onChange={(e) => changePeriodType(e.target.value)}
              style={{ ...evidenceHeaderSelectStyle, minWidth: '160px' }}
            >
              <option value="">Select Period Type</option>
              <option value="month">Monthly</option>
              <option value="week">Weekly</option>
            </select>

            <label style={evidenceHeaderControlLabelStyle}>Periods:</label>
            <select 
              value={numberOfPeriods} 
              onChange={(e) => changeNumPeriods(parseInt(e.target.value))}
              style={{ ...evidenceHeaderSelectStyle, minWidth: '128px' }}
            >
              <option value={3}>3 Periods</option>
              <option value={6}>6 Periods</option>
              <option value={9}>9 Periods</option>
              <option value={12}>12 Periods</option>
            </select>

            <label style={evidenceHeaderControlLabelStyle}>Duration:</label>
            <select
              value={chartDurationFormat}
              onChange={(e) => setChartDurationFormat(e.target.value)}
              style={{ ...evidenceHeaderSelectStyle, minWidth: '145px' }}
              title="Controls duration formatting in metric history charts"
            >
              <option value="compact">Days / hours</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>

            <span style={{ ...evidenceHeaderMetaStyle, marginLeft: 8 }}>Click a metric to view the history</span>
          </>
        )}
      />

      {/* Categorised Metric Sections */}
      {categoriesWithVisibleMetrics.map((category) => (
        <div key={category.key} style={{ marginBottom: '12px' }}>
          {/* Category Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '7px',
            paddingBottom: '6px',
            borderBottom: `2px solid ${category.accentColor}22`
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: `linear-gradient(135deg, ${category.accentColor}22 0%, ${category.accentColor}44 100%)`,
              border: `1px solid ${category.accentColor}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: category.accentColor,
              flexShrink: 0
            }}>
              <category.Icon />
            </div>
            <span style={{
              fontSize: '13px',
              fontWeight: '700',
              color: category.accentColor,
              letterSpacing: '0.4px',
              textTransform: 'uppercase'
            }}>
              {category.label}
            </span>
            <div style={{
              flex: 1,
              height: '1px',
              background: `linear-gradient(to right, ${category.accentColor}33, transparent)`
            }} />
          </div>

          {/* Metrics Grid */}
          <div style={styles.grid}>
            {category.metrics.map((metric) => {
              const metricValid = isMetricValid(displayData, metric.key);
              const trend = metricValid ? getTrend(metric.value, previousDisplayData?.[metric.key], metric.key) : null;
              const invalidReason = getMetricInvalidReason(displayData, metric.key);
              return (
                <div
                  key={metric.key}
                  style={{ ...styles.card, ...(metricValid ? {} : styles.cardDisabled), position: 'relative' }}
                  onClick={() => {
                    if (!metricValid) return;
                    setFindingContext({
                      source: 'Evidence - Metrics',
                      objectType: 'Metric',
                      objectLabel: metric.title,
                      objectId: metric.key,
                      category: category.label?.includes('Compliance') ? 'Compliance' : category.label?.includes('Cost') ? 'Cost' : 'Performance',
                    });
                    setSelectedMetric(metric);
                  }}
                  title={metricValid ? '' : invalidReason}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div style={{ ...styles.iconContainer, backgroundColor: metricValid ? metric.color : '#A5ADBA' }}>
                      <metric.icon />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {metric.description && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActivePopover(activePopover?.key === metric.key ? null : metric);
                          }}
                          title="What is this metric?"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            width: 22, height: 22, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 15, fontWeight: 700,
                            color: activePopover?.key === metric.key ? metric.color : '#97A0AF',
                            backgroundColor: activePopover?.key === metric.key ? `${metric.color}18` : 'transparent',
                            transition: 'all 0.15s',
                            flexShrink: 0, padding: 0,
                          }}
                        >ⓘ</button>
                      )}
                      <div style={{ ...styles.metricValue, ...(metricValid ? {} : styles.metricValueDisabled) }}>{metricValid ? formatDurationForMetric(metric.key, metric.value ?? 0, metric.format, chartDurationFormat) : '—'}</div>
                    </div>
                  </div>
                  <div style={{ paddingTop: '2px' }}>
                    <div style={{ ...styles.metricTitle, ...(metricValid ? {} : styles.metricTitleDisabled), marginBottom: '2px' }}>{metric.title}</div>
                    <div style={styles.metricSubtitle}>{metric.subtitle}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {metricValid && trend && (
                        <div style={{
                          ...styles.trendIndicator,
                          backgroundColor: trend.direction === 'up' ? '#E3FCEF' :
                                         trend.direction === 'down' ? '#FFEBE6' : '#DEEBFF',
                          color: trend.direction === 'up' ? '#006644' :
                                trend.direction === 'down' ? '#BF2600' : '#0052CC'
                        }}>
                          <span>{trend.direction === 'up' ? '↗' : trend.direction === 'down' ? '↘' : '→'}</span>
                          <span>{trend.change}%</span>
                        </div>
                      )}
                    </div>
                    {!(typeof window !== 'undefined' && window.__METRICUS_JIRA_PRODUCT_EDITION__ === 'free') && <button
                      style={styles.saveFindingButton}
                      title="Save Finding"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFindingForContext(apiProvider, {
                          source: 'Evidence - Metrics',
                          objectType: 'Metric',
                          objectLabel: metric.title,
                          objectId: metric.key,
                          category: category.label?.includes('Compliance') ? 'Compliance' : category.label?.includes('Collaboration') ? 'Performance' : 'Performance',
                          evidence: '',
                          row: {
                            metric: metric.title,
                            metric_key: metric.key,
                            value: metric.value,
                            subtitle: metric.subtitle,
                            category: category.label,
                            period_type: periodType,
                            periods: numberOfPeriods,
                          },
                          title: `${metric.title} finding`,
                        });
                      }}
                    >
                      <Lightbulb size={13} />
                    </button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Metric Info Popover */}
      {activePopover && (
        <>
          <PopoverBackdrop onClose={() => setActivePopover(null)} />
          <MetricInfoPopover
            metric={activePopover}
            onClose={() => setActivePopover(null)}
          />
        </>
      )}

      {/* Metric History Modal */}
      {selectedMetric && (
        <div style={styles.modal} onClick={() => setSelectedMetric(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                <div style={{ ...styles.iconContainer, backgroundColor: selectedMetric.color, width: '32px', height: '32px' }}>
                  <selectedMetric.icon />
                </div>
                {selectedMetric.title} History
              </h2>
              <button style={styles.closeButton} onClick={() => setSelectedMetric(null)}>×</button>
            </div>
            <div style={styles.modalBody}>
              {renderHistoryChart(
                getMetricHistory(selectedMetric.key, selectedMetric.format),
                selectedMetric.color,
                selectedMetric.key
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsDashboardHistorical;